import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Button,
  Link,
  Divider,
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import { Circle } from '@mui/icons-material';
import STAR_Icon from '../../assets/STAR_Icon+Type_CMYK.png';
import Image from 'mui-image';
import { isStar2026RecommendationUrlEffective } from '../../utils/projectLinkEffectiveDates';

const STAR_NEW_RECOMMENDATION_URL_LEGACY =
  'https://peointernational.communityforce.com/Funds/FundDetails.aspx?496F49596D593768464E3537506B6F754D5442315A38332B6F6C746D564B6D463958476C6673347367426967677048664F476A4976392B68326F4B6E6A5575432B65472B65535045684E453D';

const STAR_NEW_RECOMMENDATION_URL_AUGUST_2026 =
  'https://peointernational.communityforce.com/Funds/FundDetails.aspx?496F49596D593768464E3537506B6F754D5442315A2F70592F73526C365032725538694F5A69494E2F645646367541506C4B324237536C567142395A4B387457305270736F4A456B3674413D';

interface ButtonLinkProps {
  href: string;
  label: string;
}

const ButtonLink: React.FC<ButtonLinkProps> = ({ href, label }) => (
  <Button
    size="small"
    variant="text"
    color="primary"
    href={href}
    target="_blank"
    rel="noopener"
    sx={{
      textTransform: 'none',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: 'rgba(139, 184, 232, 0.08)',
      },
      color: '#FC4C02',
    }}
  >
    {label}
  </Button>
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`star-tabpanel-${index}`}
    aria-labelledby={`star-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

const StarLandingPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  const starNewRecommendationUrl = isStar2026RecommendationUrlEffective()
    ? STAR_NEW_RECOMMENDATION_URL_AUGUST_2026
    : STAR_NEW_RECOMMENDATION_URL_LEGACY;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Hero Section with STAR Logo */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #8BB8E8 0%, #FC4C02 100%)',
          color: 'white',
          borderRadius: 3,
          p: 3,
          mb: 3,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Image
            src={STAR_Icon}
            alt="STAR Logo"
            sx={{ maxWidth: '100px' }}
            width={'40vw'}
          />
        </Box>
        <Typography
          variant="h4"
          sx={{ fontWeight: 'bold', mb: 1.5, color: '#fff' }}
        >
          STAR Scholarship
        </Typography>
        <Typography
          variant="body1"
          sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto', color: '#fff' }}
        >
          The P.E.O. STAR Scholarship is a non-renewable $2,500 scholarship for
          graduating women in their final year of high school which must be used
          in the academic year following graduation or it will be forfeited. All
          applicants are recommended by a local P.E.O. chapter.
        </Typography>
      </Paper>

      {/* Tab Navigation */}
      <Card sx={{ boxShadow: 3, borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="STAR page tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 500,
                minHeight: 64,
              },
            }}
          >
            <Tab label="Information & Resources" />
          </Tabs>
        </Box>

        {/* Information & Resources Tab */}
        <TabPanel value={tabValue} index={0}>
          {/* Local Chapter Recommendation Process */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
              mb: 4,
            }}
          >
            <CardHeader
              title="Local Chapter Recommendation Process"
              subheader="The online recommendation form is available August 15 through October 15. Follow the steps below to recommend your candidate."
              sx={{
                background: 'linear-gradient(135deg, #8BB8E8 0%, #FC4C02 100%)',
                borderBottom: '1px solid #e0e0e0',
                color: '#fff',
              }}
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            />
            <CardContent sx={{ pb: 4 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      background:
                        'linear-gradient(135deg, #8BB8E8 0%, #FC4C02 100%)',
                      color: '#fff',
                      p: 1.5,
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    Step
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      background:
                        'linear-gradient(135deg, #8BB8E8 0%, #FC4C02 100%)',
                      color: '#fff',
                      p: 1.5,
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    Resources
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      <strong>1.</strong> Start thinking about a young woman you
                      would like to recommend before August 15. Think about
                      women in your community who exhibit excellence in
                      leadership, academics, extracurricular activities,
                      community service and potential for future success.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item container xs={12} md={6} spacing={1}>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/f3e9c2d71ed94b04a15f0283110f3bf1/latest"
                      label="Policies and Procedures"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ButtonLink
                      href="https://peo-portal.com/library/document/8e775070202440d291085202ef198367/latest"
                      label="Local Chapter Resource"
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1.5 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      <strong>2.</strong> Interview your candidate. An in-person
                      interview is best, but if that is not an option, you can
                      interview your candidate via a phone call or
                      videoconference.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6} />

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      pl: 1,
                      pr: 1,
                      pt: 1,
                      height: '100%',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      <strong>3.</strong> After a successful chapter vote,
                      complete the online STAR Recommendation form. The form is
                      available between August 15 and October 15 each year.
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    The P.E.O. Executive Office will email the student a link to
                    the online application portal and instructions for logging
                    in. The student has 30 days from the date the link is sent
                    to complete and submit the Student Profile, Student Activity
                    Charts, original essay not to exceed 700 words, two
                    recommendations and unofficial high school transcript
                    (through the 11th grade/junior year).
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    Your chapter's STAR committee may meet with the student to
                    proofread her essay and offer suggestions.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    Awards are announced annually by April 30, and notification
                    is sent to all applicants and recommending chapters. The
                    P.E.O. Executive Office will mail recipient award
                    certificates to recommending chapters for presentation at a
                    high school awards ceremony or at your local chapter
                    meeting.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    Your chapter can also use the STAR Scholarship Recipient
                    Media Kit (links below) to share information about your
                    recipient with local media outlets. The kit includes a
                    photography and publicity consent form for your recipient to
                    sign.
                  </Typography>
                </Grid>

                <Grid item xs={12} textAlign="center">
                  <ButtonLink
                    href="https://peo-portal.com/library/document/05be2486dca5490f9ffb91195e9a05ad/latest"
                    label="Photography and Publicity Consent Form"
                  />
                </Grid>
                <Grid item xs={12} textAlign="center">
                  <ButtonLink
                    href="https://peo-portal.com/library/document/f2854dcb7241413f983b5b11b08b673b/latest"
                    label="News Release Sample"
                  />
                </Grid>
              </Grid>
            </CardContent>
            <CardActions sx={{ pb: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} textAlign="center">
                  <Link
                    href={starNewRecommendationUrl}
                    target="_blank"
                    rel="noopener"
                    sx={{ textDecoration: 'none' }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        px: 4,
                        py: 1.5,
                        background:
                          'linear-gradient(135deg, #8BB8E8 0%, #FC4C02 100%)',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        boxShadow: 3,
                        '&:hover': {
                          boxShadow: 6,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Start New STAR Recommendation
                    </Button>
                  </Link>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 600, mx: 'auto', mt: 1 }}
                  >
                    Click the button above to be directed to the CommunityForce
                    application system. CommunityForce is a third party vendor
                    we utilize for managing applications. Separate login
                    credentials will be required.
                    <br />
                    <Link
                      href="https://peo-portal.com/library/document/77fef27667fc4fb391e52322b710e0ff/latest"
                      target="_blank"
                      rel="noopener"
                    >
                      Instructions for starting a new online Recommendation
                      Form.
                    </Link>
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6} textAlign="center">
                  <Link
                    href="https://peointernational.communityforce.com"
                    target="_blank"
                    rel="noopener"
                    sx={{ textDecoration: 'none' }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        px: 4,
                        py: 1.5,
                        background:
                          'linear-gradient(135deg, #8BB8E8 0%, #FC4C02 100%)',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        boxShadow: 3,
                        '&:hover': {
                          boxShadow: 6,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Continue Existing STAR Recommendation
                    </Button>
                  </Link>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 600, mx: 'auto', mt: 1 }}
                  >
                    Click the button above to be directed to the CommunityForce
                    application system. CommunityForce is a third party vendor
                    we utilize for managing applications. Separate login
                    credentials will be required.
                    <br />
                    <Link
                      href="https://peo-portal.com/library/document/f4b8177901e94f51ad99f4c618d74445/latest"
                      target="_blank"
                      rel="noopener"
                    >
                      Click here for instructions for continuing an existing
                      online Recommendation Form.
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </CardActions>
          </Card>

          {/* Eligibility Requirements */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
              mb: 4,
            }}
          >
            <CardHeader
              title="Eligibility Requirements"
              subheader="A woman is eligible for consideration for a STAR Scholarship if she:"
              sx={{
                background: 'linear-gradient(135deg, #8BB8E8 0%, #FC4C02 100%)',
                color: '#fff',
                borderBottom: '1px solid #e0e0e0',
              }}
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            />
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#FC4C02',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Is recommended by a local chapter of the P.E.O. Sisterhood
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#FC4C02',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Is a citizen or legal permanent resident of the United
                      States or Canada
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#FC4C02',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Exhibits excellence in leadership, academics,
                      extracurricular activities, community service and
                      potential for future success
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Circle
                      sx={{
                        fontSize: 12,
                        color: '#FC4C02',
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      Plans to attend an accredited postsecondary education
                      institution in the United States or Canada full time or
                      part time in the fall of the academic year following her
                      high school graduation
                    </Typography>
                  </Box>
                </Grid>

                <Grid
                  item
                  container
                  xs={12}
                  justifyContent="center"
                  alignItems="center"
                  direction="column"
                >
                  <Grid
                    item
                    xs={12}
                    textAlign="center"
                    justifyContent="center"
                    alignItems="center"
                    sx={{ pb: 2 }}
                  >
                    <Image
                      src={
                        'https://www.peointernational.org/wp-content/uploads/2023/09/STAR-qualities-graphic.png'
                      }
                      alt="STAR Qualities Graphic"
                      sx={{ maxWidth: '300px' }}
                      width={'40vw'}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    sx={{ p: 1.5, lineHeight: 1.6, fontStyle: 'italic' }}
                  >
                    The applications of individuals who satisfy these
                    eligibility criteria will be evaluated on a
                    nondiscriminatory basis without consideration of race, age,
                    national origin, religious affiliation, disability or P.E.O.
                    connection.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Application Process */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <CardHeader
              title="Application Process"
              sx={{
                background: 'linear-gradient(135deg, #8BB8E8 0%, #FC4C02 100%)',
                color: '#fff',
                borderBottom: '1px solid #e0e0e0',
              }}
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            />
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    Members of a local P.E.O. chapter will interview a student
                    before deciding to recommend her.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    After a local chapter recommends a student, the P.E.O.
                    Executive Office will email the applicant instructions for
                    logging into an online account to complete her portion of
                    the application. This must be submitted no more than 30 days
                    after she receives the email.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    The application includes information about the student and
                    her extracurricular and community service/volunteer
                    activities, a 700-word essay, two letters of recommendation
                    and an unofficial high school transcript (through 11th
                    grade-/-junior year).
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    Awards are announced annually by April 30, and notification
                    is sent to all applicants and recommending chapters.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    sx={{
                      p: 1.5,
                      lineHeight: 1.6,
                      textAlign: 'center',
                      fontWeight: 'bold',
                    }}
                  >
                    Additional Resources for Students:
                  </Typography>
                </Grid>
                <Grid item xs={12} textAlign="center">
                  <ButtonLink
                    href="https://peo-portal.com/library/document/f3e9c2d71ed94b04a15f0283110f3bf1/latest"
                    label="Policies and Procedures"
                  />
                </Grid>
                <Grid item xs={12} textAlign="center">
                  <ButtonLink
                    href="https://peo-portal.com/library/document/3ad52b69a6a443b981119089ff368a02/latest"
                    label="Student Application Instructions"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>
      </Card>
      {/* Become a Constellation Chapter */}
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          overflow: 'hidden',
          mb: 4,
        }}
      >
        <CardHeader
          title="Become a Constellation Chapter"
          sx={{
            background: 'linear-gradient(135deg, #8BB8E8 0%, #FC4C02 100%)',
            borderBottom: '1px solid #e0e0e0',
            color: '#fff',
          }}
          titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
        />
        <CardContent sx={{ pb: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                Make a difference —and a bigger impact— in the lives of young
                women. The P.E.O. STAR Scholarship Constellation Chapter Program
                offers the opportunity for local chapters to further support the
                P.E.O. STAR Scholarship and promote outstanding young women as
                they enter colleges and universities. By making a lump-sum
                contribution of $500 or more, a chapter can proudly bear the
                title of a “STAR Scholarship Constellation Chapter." This
                recognition not only highlights a chapter’s dedication, but also
                gives participating P.E.O. chapters a way to showcase their
                commitment to empowering women in their higher education
                endeavors.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                To become a Constellation Chapter, make sure your chapter’s
                contribution reaches your state, provincial or district (s/p/d)
                treasurer or executive assistant by January 31. (Chapters in
                Hawaii should send their contributions to the International
                Chapter Treasurer).
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                Individuals can also play a pivotal role in this legacy journey.
                Lump-sum donations of $500 or more qualify your chapter as a
                STAR Constellation Chapter. Specify your chapter’s name on the
                Individual Donation Form to create this legacy for your chapter.
                To be considered a charitable donation, this contribution must
                be made payable to P.E.O. STAR Scholarship and be sent to the
                P.E.O. Executive Office by January 31.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                S/P/D chapter gift transmittals that include donations
                qualifying for the Constellation Chapter program must be
                received at the P.E.O. Executive Office in Des Moines no later
                than February 28/29 to be recognized at the upcoming s/p/d
                convention. Gifts received by the executive office after
                February 28/29 will be processed and recognized at the following
                year’s convention.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default StarLandingPage;
